import React, { useEffect, useState, useCallback } from "react";
import { getAxiosInstance } from "../../axiosInstance";
import Container from "react-bootstrap/Container";
import Alert from "react-bootstrap/Alert";
import Form from "react-bootstrap/Form";
import Button from "react-bootstrap/Button";
import DataTable from "react-data-table-component";
import "bootstrap/dist/css/bootstrap.min.css";
import "./TrackData.css";
import ThinFooter from "../Footer/ThinFooter"; // Import the footer component

const TrackData = () => {
    const [trackData, setTrackData] = useState([]); // Stores all records
    const [filteredData, setFilteredData] = useState([]); // Stores filtered records for display
    const [userRole, setUserRole] = useState(null);
    const [userFullId, setUserFullId] = useState(null);
    const [currentPage, setCurrentPage] = useState(1);
    const [error, setError] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [loadingDownload, setLoadingDownload] = useState(false);
    const [loadingDelete, setLoadingDelete] = useState(false);

    const recordsPerPage = 100; // Displaying 100 records per page

    // ✅ Fetch User Role & UUID
    useEffect(() => {
        const fetchUserRole = async () => {
            const token = localStorage.getItem("token");
            if (!token) return;

            try {
                const axiosInstance = getAxiosInstance("api");
                const response = await axiosInstance.get("/users/currentUser", {
                    headers: { Authorization: token },
                });

                if (response.data.success && response.data.data.user) {
                    const user = response.data.data.user;
                    setUserRole(user.role);
                    //setUserUUID(user.uuid);
                    setUserFullId(`${user.firstName}_${user.lastName}_${user.id}`); 
                }
            } catch (error) {
                console.error("Error fetching user role:", error);
            }
        };
        fetchUserRole();
    }, []);

    // ✅ Fetch Tracking Data
    const fetchTrackData = useCallback(async () => {
        if (!userRole) return;

        try {
            const axiosInstance = getAxiosInstance("tracking");
            let apiUrl = userRole === "User" && userFullId 
                ? `/tracking_data?car=${userFullId}&limit=3000` 
                : userRole === "Admin" 
                ? "/tracking_data?limit=5000" 
                : null;

            if (!apiUrl) {
                console.log("Invalid role detected:", userRole);
                return;
            }

            console.log("Fetching data from API:", apiUrl);
            const response = await axiosInstance.get(apiUrl);

            if (response.data?.data) {
            const formattedData = response.data.data.map((item) => ({
                ...item,
                date: item.date.split(" 00:00:00 GMT")[0],
                deviceId: item.car,
                version: item.version,
                userName: item.car.replace(/car_/, ""), // Extract username
            }));

                setTrackData(formattedData);
                setFilteredData(formattedData);
            } else {
                setError("No tracking data found.");
            }
        } catch (err) {
            console.error("API Error:", err.response ? err.response.data : err.message);
            setError(err.response?.data?.error?.message || "An unknown error occurred.");
        }
    }, [userRole, userFullId]);

    useEffect(() => {
        if (!userRole) return;

        fetchTrackData();
        const interval = setInterval(fetchTrackData, 3000);

        return () => clearInterval(interval);
    }, [fetchTrackData, userRole, userFullId]);

    // ✅ Handle search
    useEffect(() => {
        if (searchTerm) {
            const filtered = trackData.filter((item) =>
                Object.values(item).some((value) =>
                    value?.toString().toLowerCase().includes(searchTerm.toLowerCase())
                )
            );
            setFilteredData(filtered);
            setCurrentPage(1);
        } else {
            setFilteredData(trackData);
        }
    }, [searchTerm, trackData]);

    // ✅ Paginate the filtered data
    const paginatedData = filteredData.slice((currentPage - 1) * recordsPerPage, currentPage * recordsPerPage);

    // ✅ Download CSV Handler (Only for Admin)
    const handleDownloadCSV = async () => {
        if (userRole !== "Admin") return; // Ensure only Admin can use

        setLoadingDownload(true); // Set only download button loading
        try {
            const axiosInstance = getAxiosInstance("tracking");
            const response = await axiosInstance.get("/download_csv", {
                responseType: "blob",
            });

            const url = window.URL.createObjectURL(new Blob([response.data]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "tracking_data.csv");
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
        } catch (error) {
            console.error("Error downloading CSV:", error);
        }
        setLoadingDownload(false);
    };

    // ✅ Delete All Rows Handler (Only for Admin)
    const handleDeleteAll = async () => {
        if (userRole !== "Admin") return; // Ensure only Admin can use
        if (!window.confirm("Are you sure you want to delete all tracking data?")) return;

        setLoadingDelete(true); // Set only delete button loading
        try {
            const axiosInstance = getAxiosInstance("tracking");
            await axiosInstance.delete("/delete_all_data");

            alert("All tracking data deleted successfully.");
            fetchTrackData(); // Refresh the table after deletion
        } catch (error) {
            console.error("Error deleting data:", error);
            alert("Failed to delete data.");
        }
        setLoadingDelete(false);
    };

    return (
        <>
            <Container fluid className="p-3 d-flex justify-content-center">
                <div style={{ width: "80%", marginLeft: "240px" }}>
                    <h2 className="text-center mb-4">Track Data</h2>

                    {error && <Alert variant="danger" className="text-center">{error}</Alert>}

                    {/* ✅ Search Bar */}
                    <div className="d-flex justify-content-between align-items-center mb-3">
                        <Form.Control
                            type="text"
                            placeholder="Search Tracking Data..."
                            className="flex-grow-1"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            style={{ maxWidth: userRole === "Admin" ? "75%" : "100%" }}
                        />

                        {userRole === "Admin" && (
                            <div className="d-flex">
                                 {/*<Button
                                    variant="success"
                                    className="me-2 sv_btn db1"
                                    onClick={handleDownloadCSV}
                                    disabled={loadingDownload} // Only disable Download button
                                >
                                    {loadingDownload ? "Downloading..." : "Download CSV"}
                                </Button> */}
                                
                                <Button
                                    variant="danger"
                                    onClick={handleDeleteAll}
                                    disabled={loadingDelete} // Only disable Delete button
                                >
                                    {loadingDelete ? "Deleting..." : "Delete All"}
                                </Button>
                            </div>
                        )}
                    </div>

                    <DataTable
                        columns={[
                            { name: "DeviceId", selector: (row) => row.deviceId || "N/A", sortable: true, minWidth: "190px" },
                            { name: "Version", selector: (row) => row.version || "N/A", sortable: true, minWidth: "100px" },
                            { name: "UserName", selector: (row) => row.userName || "N/A", sortable: true, minWidth: "170px" },
                            {
                                name: "Latitude",
                                sortable: true,
                                minWidth: "150px",
                                cell: row => (
                                    <a
                                        href={`https://maps.google.com/?q=${row.latitude},${row.longitude}`}
                                        target="_blank"
                                        className="lt_lnk"
                                        rel="noopener noreferrer"
                                    >
                                        {row.latitude || "N/A"}
                                    </a>
                                )
                            },
                            {
                                name: "Longitude",
                                sortable: true,
                                minWidth: "150px",
                                cell: row => (
                                    <a
                                        href={`https://maps.google.com/?q=${row.latitude},${row.longitude}`}
                                        target="_blank"
                                        className="lt_lnk"
                                        rel="noopener noreferrer"
                                    >
                                        {row.longitude || "N/A"}
                                    </a>
                                )
                            },
                            { name: "Time", selector: (row) => row.time || "N/A", sortable: true, minWidth: "150px" },
                            { name: "Date", selector: (row) => row.date || "N/A", sortable: true, minWidth: "200px" },
                        ]}
                        data={paginatedData}
                        pagination
                        paginationServer
                        paginationTotalRows={filteredData.length}
                        paginationPerPage={recordsPerPage}
                        paginationDefaultPage={1}
                        onChangePage={setCurrentPage}
                        paginationRowsPerPageOptions={[100]}
                        paginationComponentOptions={{
                            rowsPerPageText: "Records per page:",
                            rangeSeparatorText: "of",
                            selectAllRowsItem: false,
                        }}
                        highlightOnHover
                        responsive
                        striped
                        fixedHeader
                        fixedHeaderScrollHeight="500px"
                        noDataComponent="No tracking data found."
                        customStyles={{
                            table: { style: { width: "100%" } },
                            rows: { style: { fontSize: "16px", padding: "12px" } },
                            headCells: { style: { backgroundColor: "#f8f9fa", fontWeight: "bold", fontSize: "17px" } },
                        }}
                    />
                </div>
            </Container>
            <ThinFooter />
        </>
    );
};

export default TrackData;
